import React, { useEffect, useState } from 'react';
import SummaryApi from '../common';
import axios from 'axios';
//import displayINRCurrency from '../helpers/displayCurrency';
import { Link } from 'react-router-dom';
//import { generateGSTInvoice } from "../helpers/generateGSTInvoice";


/**
 * AllOrders.jsx
 * Admin page that lists orders with search, filters, status update and export to CSV
 */

const STATUS_OPTIONS = [
  "all",
  "placed",
  "processing",
  "shipped",
  "delivered",
  "cancelled",
  "paid"
];

const AllOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [filtered, setFiltered] = useState([]);
  const [updatingOrderId, setUpdatingOrderId] = useState(null);

  const fetchAllOrders = async () => {
    setLoading(true);
    try {
      const res = await axios.get(SummaryApi.getAllOrders.url, { withCredentials: true });
      // backend returns { orders: [...] } in your routes
      const list = res.data?.orders || res.data?.data || res.data || [];
      setOrders(Array.isArray(list) ? list : []);
    } catch (err) {
      //console.error("Failed to fetch orders:", err?.response?.data || err.message);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllOrders();
  }, []);

  // derive filtered list when orders / search / statusFilter change
  useEffect(() => {
    const q = (search || "").trim().toLowerCase();

    let list = Array.isArray(orders) ? orders.slice() : [];

    if (statusFilter && statusFilter !== "all") {
      list = list.filter(o => {
        const s = (o.status || o.paymentStatus || "").toString().toLowerCase();
        return s.includes(statusFilter.toLowerCase());
      });
    }

    if (q) {
      list = list.filter(o => {
        const orderNumber = (o.orderNumber || "").toString().toLowerCase();
        const orderId = (o._id || "").toString().toLowerCase();
        const userId = (o.userId || "").toString().toLowerCase();
        const paymentId = (o.paymentId || "").toString().toLowerCase();
        
        const itemMatch = (o.items || []).some(it =>
          (it.code || "").toLowerCase().includes(q) ||
          (it.name || "").toLowerCase().includes(q)
        );
        
        return (
          orderNumber.includes(q) ||
          orderId.includes(q) ||
          userId.includes(q) ||
          paymentId.includes(q) ||
          itemMatch
        );
      });
    }

    setFiltered(list);
  }, [orders, search, statusFilter]);

  const exportToCSV = () => {
    const data = filtered.length ? filtered : orders;
    if (!Array.isArray(data) || data.length === 0) {
      alert("No orders to export");
      return;
    }

    const header = [
      "orderId",
      "orderNumber",
      "userId",
      "subtotal",
      "deliveryCharge",
      "totalAmount",
      "status",
      "paymentStatus",
      "paymentId",
      "createdAt",
      "address_fullName",
      "address_line",
      "address_city",
      "address_pincode",
      "items"
    ];

    const rows = data.map(o => {
      const address = o.address || {};
      const itemsText = (o.items || []).map(it => `${it.name} [{${it.code || "NA"}] x ${it.qty} (₹${it.price})`).join(" | ");
      return [
        o._id,
        o.orderNumber,
        o.userId || "",
        o.subtotal ?? o.amount,
        o.deliveryCharge ?? 0,
        o.totalAmount ?? o.amount,
        o.status || "",
        o.paymentStatus || "",
        o.paymentId || "",
        o.createdAt ? new Date(o.createdAt).toLocaleString() : "",
        address.fullName || "",
        address.addressLine || "",
        address.city || "",
        address.pincode || "",
        itemsText
      ];
    });

    // build CSV string
    const csvContent = [
      header.join(","),
      ...rows.map(r => r.map(cell => `"${(cell ?? "").toString().replace(/"/g, '""')}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `orders_export_${new Date().toISOString().slice(0,19).replace(/[:T]/g,'-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    if (!orderId) return;
    setUpdatingOrderId(orderId);
    try {
      await axios.put(SummaryApi.updateOrderStatus(orderId), { status: newStatus }, { withCredentials: true });
      // refresh list after update
      await fetchAllOrders();
    } catch (err) {
      //console.error("Failed to update order status:", err?.response?.data || err.message);
      alert("Failed to update order status");
    } finally {
      setUpdatingOrderId(null);
    }
  };

  // simple order card UI to match AllProducts style
  const OrderCard = ({ order }) => {
    const address = order.address || {};
    return (
      <div className='w-full bg-white p-4 border border-slate-300 rounded' style={{ minWidth: 360 }}>
        <div className='flex justify-between items-start'>
          <div>
            <div className='font-semibold'>Order #{order.orderNumber || order._id}</div>
            <div className='text-sm text-slate-600'>ID: {order._id}</div>
            <div className='text-sm text-slate-600'>User: {order.userId || "Guest"}</div>
            <div className='text-sm text-slate-600 mt-1'>Subtotal: ₹{order.subtotal ?? order.amount}</div>
            <div className='text-sm text-slate-600'>Delivery: ₹{order.deliveryCharge ?? 0}</div>
            <div className='text-sm text-slate-800 font-semibold'>Total Paid: ₹{order.totalAmount ?? order.amount}</div>
            <div className='text-sm text-slate-600'>Payment: {order.paymentStatus || "N/A"}</div>
          </div>

          <div className='text-right'>
            <div className='mb-2'>
              <select
                value={order.status || ""}
                onChange={(e) => updateOrderStatus(order._id, e.target.value)}
                disabled={updatingOrderId === order._id}
                className='border rounded px-2 py-1 text-sm'
              >
                <option value=''>Set status</option>
                {STATUS_OPTIONS.filter(s => s !== "all").map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
            <div className='flex flex-col gap-2'>
              <Link to={`/order-success/${order._id}`}>
                <button className='px-5 py-1 bg-[#5a3620] text-white rounded'>View</button>
              </Link>
            <button
              onClick={() => {
                const url = `${SummaryApi.generateInvoice(order._id).url}?force=true`;
                window.open(url, "_blank");
              }}
              className="px-3 py-1 border border-[#5a3620] text-[#5a3620] rounded"
            >
              Download Invoice
            </button>

            </div>
          </div>
        </div>

        <hr className='my-2' />

        <div className='text-sm'>
          <div className='font-semibold'>Items:</div>
          {(order.items || []).map((it, i) => (
<div key={i} className="text-slate-700">
    {it.name} 
    <span className="ml-2 text-xs text-slate-500">
      (Code: {it.code || "NA"})
    </span>
    × {it.qty} — ₹{Number(it.price || 0) * Number(it.qty || 1)}
    <span className="text-xs text-slate-500 ml-2">
      (GST {it.gst ?? 18}%)
    </span>
  </div>          ))}
        </div>

        <div className='mt-2 text-sm'>
          <div className='font-semibold'>Shipping:</div>
          {address.addressLine ? (
            <div>
              <div>{address.fullName}</div>
              <div>{address.addressLine}, {address.city} - {address.pincode}</div>
              <div>Phone: {address.phone}</div>
            </div>
          ) : <div>No address</div>}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className='bg-white py-2 px-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3' style={{fontFamily: 'segoe'}}>
        <h2 className='font-bold text-lg'>All Orders</h2>

        <div className='flex gap-2 items-center'>
          <input
            type='text'
            placeholder='Search order id / number / user id'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className='border rounded px-3 py-1'
          />

          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className='border rounded px-3 py-1'>
            {STATUS_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>

          <button className='border-2 border-[#5a3620] text-[#5a3620] hover:bg-[#5a3620] hover:text-white transition-all py-2 px-3 rounded-full' onClick={exportToCSV}>Export CSV</button>
        </div>
      </div>

      <div className='flex items-start flex-wrap gap-5 py-4 h-[calc(100vh-200px)] overflow-y-scroll px-4'>
        {loading ? (
          <div>Loading orders...</div>
        ) : (
          (filtered.length ? filtered : orders).map((order, idx) => (
            <OrderCard order={order} key={order._id || idx} />
          ))
        )}
      </div>
    </div>
  );
};

export default AllOrders;
